<template>
  <button
    type="button"
    data-test-id="dashboard-future-apt-tab"
    class="dashboard-tab-list-item"
    :class="[tabClass, { active: isActive, 'has-check-icon': showCheckIcon }]"
    role="tab"
    :id="tabElementId || undefined"
    :aria-label="computedAriaLabel"
    :aria-selected="isActive ? 'true' : 'false'"
    :tabindex="isActive ? 0 : -1"
    @click="$emit('onTabClick', value)"
  >
    <span v-if="icon" class="tab-icon" :class="icon"></span>
    <span v-html="label"></span>
    <img
      v-if="showCheckIcon"
      :src="checkIconSrc"
      alt=""
      class="tab-check-icon"
    />
    <span
      v-if="count && stats && stats[count]"
      class="counter-badge"
      :style="{
        fontSize: stats[count] > 99 ? '10px' : '12px'
      }"
    >
      {{ stats[count] }}
    </span>
  </button>
</template>

<script>
export default {
  name: "DashboardTabListItem",
  props: {
    label: {
      type: String,
      default: ""
    },
    icon: {
      type: String,
      default: ""
    },
    type: {
      type: String,
      default: ""
    },
    value: {
      type: String,
      default: ""
    },
    tabClass: {
      type: String,
      default: ""
    },
    count: {
      type: String,
      default: ""
    },
    isActive: {
      type: Boolean,
      default: false
    },
    stats: {
      type: Object,
      default: () => ({})
    },
    showCheckIcon: {
      type: Boolean,
      default: false
    },
    checkIconSrc: {
      type: String,
      default: () => require("@/assets/svg/check.svg")
    },
    tabElementId: {
      type: String,
      default: ""
    }
  },
  emits: ["onTabClick"],
  computed: {
    computedAriaLabel() {
      const safeLabel = this.label
        ?.replace(/<[^>]*>/g, " ")
        ?.replace(/\s+/g, " ")
        ?.trim()
      const safeCount = this.count && this.stats ? this.stats[this.count] : null
      if (safeCount === null || safeCount === undefined || safeCount === "") {
        return safeLabel || "Dashboard tab"
      }
      return `${safeLabel || "Dashboard tab"}, ${safeCount}`
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables.scss";
@import "@/assets/v3scss/_variables.scss";

.dashboard-tab-list-item {
  position: relative;
  cursor: pointer;
  display: flex;
  font: inherit;
  color: inherit;
  text-align: left;
  appearance: none;
  border: none;
  background: transparent;
  margin: 0;
  font-family: inherit;
  line-height: inherit;
  align-items: center;
  justify-content: center;
  padding: 16px 8px;
  gap: 8px;
  width: 100px;
  background-color: white;
  border-right: 1px solid $gray-light;
  color: $gray-darken-50;
  text-align: left;
  font-size: 12px;

  &.active {
    background-color: $cyan-light;
    border-bottom: 3px solid $accent-blue-dark;
    border-right: none;
  }

  &:focus-visible {
    outline: 2px solid $outline-color;
    outline-offset: $outline-offset;
    box-shadow:
      0 0 0 2px $white,
      0 0 0 4px $outline-color;
    z-index: 1;
  }

  &.has-check-icon {
    width: 120px;
    min-width: 120px;
    justify-content: space-between;
  }

  .tab-check-icon {
    position: relative;
    width: 14px;
    height: 14px;
  }

  .counter-badge {
    position: absolute;
    top: -8px;
    right: 16px;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 14px;
    font-weight: bold;
    color: $black;
    background-color: $white;
    border: 1px solid $gray-light;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &.red {
    background-color: rgba($red, 0.1);
    &.active {
      background-color: rgba($red, 0.4);
      border-bottom: 3px solid $red-dark;
      border-right: none;
    }
    .counter-badge {
      color: $white;
      background-color: $red;
    }
  }

  &.green {
    background-color: rgba($orange-light, 0.1);
    &.active {
      background-color: rgba($orange-light, 0.4);
      border-bottom: 3px solid $accent-blue-dark;
      border-right: none;
    }
    .counter-badge {
      color: $white;
      background-color: $orange-light;
    }
  }

  &.purple {
    background-color: rgba($purple, 0.05);
    &.active {
      background-color: rgba($purple, 0.4);
      border-bottom: 3px solid $accent-blue-dark;
      border-right: none;
    }
    .counter-badge {
      color: $white;
      background-color: $purple-badge;
    }
  }
}

@media only screen and (max-width: 575px) {
  .dashboard-tab-list-item {
    width: calc(50% - 4px);
    min-width: 150px;
  }
}
</style>
